import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ShieldCheck, CheckCircle } from "@phosphor-icons/react";

// Blocking GDPR consent gate. Rendered inside the authenticated shell: if the
// signed-in user has not accepted the current policy version, we require an
// explicit, recorded consent before they can use the app.
export default function ConsentGate() {
  const [needed, setNeeded] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/consents/current")
      .then((r) => { if (alive) setNeeded(!!r.data.needs_consent); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/consents/accept", {});
      setNeeded(false);
    } catch {
      setBusy(false);
    }
  };

  if (!needed) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 backdrop-blur-sm px-4"
      data-testid="consent-gate"
    >
      <div className="glass rounded-2xl max-w-lg w-full p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "#FFB020" }}>
            <ShieldCheck size={24} weight="duotone" color="#050B0A" />
          </div>
          <div>
            <div className="eyebrow text-[10px]">Data protection</div>
            <div className="font-display text-xl">Your consent, on the record</div>
          </div>
        </div>

        <p className="text-white/70 text-sm leading-relaxed">
          PerCen360 processes your financial and compliance data. Before you continue,
          please review and agree to how we handle it. Your consent is recorded with a
          timestamp so it can be demonstrated (GDPR Art. 7).
        </p>

        <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm">
          <Link to="/privacy" target="_blank" className="text-[#FFB020] underline" data-testid="consent-link-privacy">Privacy Policy</Link>
          <Link to="/terms" target="_blank" className="text-[#FFB020] underline" data-testid="consent-link-terms">Terms &amp; Conditions</Link>
          <Link to="/dpia" target="_blank" className="text-[#FFB020] underline" data-testid="consent-link-dpia">DPIA Summary</Link>
        </div>

        <button
          type="button"
          onClick={() => setAgreed((v) => !v)}
          className="mt-6 flex items-start gap-3 text-left w-full"
          data-testid="consent-checkbox"
          aria-pressed={agreed}
        >
          <CheckCircle
            size={22}
            weight={agreed ? "fill" : "regular"}
            color={agreed ? "#00A86B" : "rgba(255,255,255,0.4)"}
            className="mt-0.5 shrink-0"
          />
          <span className="text-sm text-white/80">
            I have read and agree to the Privacy Policy, Terms &amp; Conditions, and DPIA,
            and I consent to the processing of my data as described.
          </span>
        </button>

        <button
          type="button"
          disabled={!agreed || busy}
          onClick={accept}
          className="btn-gold w-full mt-6 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="consent-accept-button"
        >
          {busy ? "Recording…" : "Agree & continue"}
        </button>
      </div>
    </div>
  );
}
